import axios from "axios";

// Tenant context: ?tenant= on initial load is persisted; X-Tenant header is
// sent on every request. Production subdomains (Host header) take priority
// server-side; "vertex" is the preview default workspace.
try {
  const urlTenant = new URLSearchParams(window.location.search).get("tenant");
  if (urlTenant) localStorage.setItem("aetria_tenant", urlTenant.toLowerCase());
} catch {}

/* Explicit tenant (?tenant= / subdomain) vs. login-resolved workspace:
   the base-domain general login stores the workspace it resolved under
   aetria_ws — it themes and scopes API calls but does NOT make the surface
   white-label (isTenantSurface stays false, logout returns to Aetria OS). */
export const getExplicitTenant = () => localStorage.getItem("aetria_tenant") || "";
export const getTenant = () =>
  getExplicitTenant() || localStorage.getItem("aetria_ws") || "";
export const setResolvedWorkspace = (subdomain) => {
  try { localStorage.setItem("aetria_ws", subdomain); } catch {}
};
export const clearResolvedWorkspace = () => {
  try { localStorage.removeItem("aetria_ws"); } catch {}
};

/* True ONLY on the production base domain (aetriaos.com / www) — used to
   hide demo affordances from real prospects. Preview domains keep them. */
export const isProductionBaseHost = () => {
  try {
    const host = window.location.hostname.toLowerCase();
    return host === "aetriaos.com" || host === "www.aetriaos.com";
  } catch {
    return false;
  }
};

/* Base-domain vs tenant-subdomain surface detection (root route behavior):
   tenant subdomains (and explicit ?tenant= selections) get the branded
   login/app; the bare base/preview domain gets the product landing page. */
export const isTenantSurface = () => {
  try {
    if (localStorage.getItem("aetria_tenant")) return true;
    const host = window.location.hostname.toLowerCase();
    const base = "aetriaos.com";
    if (host === base || host === `www.${base}`) return false;
    if (host.endsWith(`.${base}`)) {
      const sub = host.slice(0, -(base.length + 1));
      return Boolean(sub) && !sub.includes(".")
        && !["www", "app", "api", "platform", "admin", "aetria"].includes(sub);
    }
    return false; // preview / unknown domains behave as the base domain
  } catch {
    return false;
  }
};

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true, // send the httpOnly trusted-device refresh cookie
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("aetria_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers["X-Tenant"] = getTenant();
  return config;
});

// silent refresh: on a 401, try the rotating refresh cookie once, then retry.
let isRefreshing = false;
let waiters = [];
const skipRefresh = (url = "") =>
  url.includes("/auth/refresh") || url.includes("/auth/login")
  || url.includes("/auth/logout") || url.includes("/auth/general");

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config || {};
    const status = err.response?.status;
    const code = err.response?.data?.code;
    const blocked = ["tenant_suspended", "tenant_pending", "tenant_rejected"];
    if (status === 403 && blocked.includes(code)
        && window.location.pathname !== "/suspended") {
      try {
        sessionStorage.setItem("aetria_block", JSON.stringify(
          { code, detail: err.response?.data?.detail || "" }));
      } catch { /* ignore */ }
      window.location.href = "/suspended";
      return Promise.reject(err);
    }

    if (status === 401 && !original._retry && !skipRefresh(original.url)) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => waiters.push({ resolve, reject }))
          .then(() => api(original));
      }
      original._retry = true;
      isRefreshing = true;
      try {
        const { data } = await api.post("/auth/refresh");
        localStorage.setItem("aetria_token", data.access_token);
        waiters.forEach((w) => w.resolve());
        waiters = [];
        return api(original);
      } catch (refreshErr) {
        waiters.forEach((w) => w.reject(refreshErr));
        waiters = [];
        localStorage.removeItem("aetria_token");
        if (window.location.pathname !== "/login") window.location.href = "/login";
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(err);
  }
);

export async function downloadFile(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
